import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { WEBSITE_SUCCEEDED, WEBSITE_UPDATE } from '@va/dashboard/actions/api';
import { UserRole, WebsiteResponse } from '@va/types/website';

export type ExtraWebsiteInfo = {
  role: UserRole;
  visitLimitReachedAt: number[];

  // Standalone specific fields
  unlimitedPlanActive?: boolean;
};

export type ActiveWebsiteSliceData = {
  core: WebsiteResponse;
  extra: ExtraWebsiteInfo;
};

type ActiveWebsiteSlice = {
  data: ActiveWebsiteSliceData;

  isLoading: boolean;
  error?: string;
};

const initialState: ActiveWebsiteSlice = {
  isLoading: false,
  data: {
    core: {} as WebsiteResponse,
    extra: {
      role: '' as UserRole,
      visitLimitReachedAt: [],
    },
  },
  error: undefined,
};

/**
 * Slice representing the currently active website.
 *
 * The state is populated from two separate API requests:
 *
 * 1. GET /users/{userId}/websites/{websiteId}
 *    - Populates `core`: the main website details
 *
 * 2. GET /users/{userId}/websites (filtered by website)
 *    - Populates `extra`: supplementary info not available in the core endpoint,
 *      primarily the current user's role on the website
 *
 *
 * TODOS:
 *  - remove the still existing website state from  ['core','website']
 *  - for 3as, consider adding intp website details here as well
 *  - cleanup old selectors, actions and sagas ( getWebsite, getWebsiteSaga )
 */

export const activeWebsiteSlice = createSlice({
  name: 'activeWebsite',
  initialState: initialState,
  reducers: {
    setData(state, action: PayloadAction<ActiveWebsiteSliceData>) {
      state.isLoading = false;
      state.error = undefined;

      state.data.core = action.payload.core;
      state.data.extra = action.payload.extra;
    },
    setExtraInfo(state, action: PayloadAction<ExtraWebsiteInfo>) {
      state.data.extra = action.payload;
    },
    setCoreData(state, action: PayloadAction<WebsiteResponse>) {
      state.data.core = action.payload;
    },
    setVisitLimitReachedAt(state, action: PayloadAction<number[]>) {
      state.data.extra.visitLimitReachedAt = action.payload;
    },
    setIsLoading(state, action: PayloadAction<boolean>) {
      state.error = undefined;
      state.isLoading = action.payload;
    },
    setError(state, action: PayloadAction<string | undefined>) {
      state.error = action.payload;
    },
  },
  selectors: {
    selectIsLoading: (state) => state.isLoading,
    selectData: (state) => state.data,
    isInitialized: (state) => state.data.core.id && state.data.extra.role,
    selectExtraInfo: (state) => state.data.extra,
    selectCoreInfo: (state) => state.data.core,
    selectWebsiteRole: (state) => state.data.extra.role,
    isWebsiteContributor: (state) => !!state.data.extra.role && state.data.extra.role !== UserRole.OWNER,
    hasUserRole: (state) => (userRole: UserRole) => state.data.extra.role === userRole,
    isWebsiteOwner: (state) => state.data.extra.role === UserRole.OWNER,
    selectVisitLimitReachedAt: (state) => state.data.extra.visitLimitReachedAt,
  },
  extraReducers: (builder) => {
    // Backup for populating the core website state
    builder.addMatcher(
      (action) => action.type === WEBSITE_SUCCEEDED,
      (state, action: { data: WebsiteResponse }) => {
        if (action.data) {
          state.data.core = action.data;
        }
      },
    );

    // Handle `WEBSITE_UPDATE` action from old website reducer
    builder.addMatcher(
      (action) => action.type === WEBSITE_UPDATE,
      (state, action: { data: Partial<WebsiteResponse> }) => {
        if (action.data) {
          state.data.core = { ...state.data?.core, ...action.data };
        }
      },
    );
  },
});
